import { useEffect, useState } from "react";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import SectionHeading from "@/components/SectionHeading";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { fetchProducts, openBooking } from "@/lib/api";
import { BRIDAL_CATEGORIES, waLink, WA_MESSAGES } from "@/config/site";
import { Link } from "react-router-dom";

const BridalPage = () => {
    const [items, setItems] = useState([]);
    useEffect(() => {
        fetchProducts({ collection: "bridal", limit: 8 }).then((d) => setItems(d.items)).catch(() => {});
    }, []);

    return (
        <main>
            <Seo
                title="Bridal Jewellery in Amritsar — Bridal Sets, Rani Haar, Chokers | BSK Jewellers"
                siteName="BSK Jewellers"
                description="Bridal jewellery at BSK Jewellers, Rani Ka Bagh, Amritsar — bridal sets, chokers, rani haar, earrings, bangles, maang tikka and custom bridal jewellery. Book a bridal consultation."
                image="/images/bridal-new.jpg"
            />
            <section className="relative h-[80svh] min-h-[520px] overflow-hidden grain" data-testid="bridal-hero">
                <div className="hero-kenburns w-full h-full">
                    <img src="/images/bridal-new.jpg" alt="Indian bride wearing BSK Jewellers bridal gold, Amritsar" className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/30" />
                <div className="relative h-full max-w-[1500px] mx-auto px-5 sm:px-8 flex flex-col justify-end pb-24">
                    <span className="eyebrow mb-5">The Bridal Atelier · Amritsar</span>
                    <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-3xl">
                        <MaskedLine delay={0.2}>For the Bride.</MaskedLine>
                        <MaskedLine delay={0.35}>For the Moment. <span className="italic gold-text">Forever.</span></MaskedLine>
                    </h1>
                    <Reveal delay={0.6}>
                        <p className="text-sand text-sm sm:text-base max-w-lg mt-6 leading-relaxed">
                            Jewellery created to become part of the memories you'll carry for a lifetime.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="py-20 sm:py-28">
                <div className="max-w-[1500px] mx-auto px-5 sm:px-8">
                    <SectionHeading eyebrow="Bridal Categories" title={<>Everything for the <span className="italic gold-text">Punjabi Bride</span></>} />
                    <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
                        {BRIDAL_CATEGORIES.map((c) => (
                            <span key={c} data-testid={`bridal-page-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-[11px] tracking-[0.22em] uppercase text-ivory/85 border border-gold/25 px-5 py-2.5">
                                {c}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {items.length > 0 && (
                <section className="pb-24 sm:pb-32">
                    <div className="max-w-[1500px] mx-auto px-5 sm:px-8">
                        <SectionHeading eyebrow="The Collection" title={<>Bridal Pieces, <span className="italic gold-text">Handpicked</span></>} />
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                            {items.map((p, i) => (
                                <Reveal key={p.slug} delay={(i % 4) * 0.07}>
                                    <ProductCard product={p} />
                                </Reveal>
                            ))}
                        </div>
                        <Reveal className="text-center mt-12">
                            <Link to="/collections?collection=bridal" data-testid="bridal-view-all-btn" className="text-[11px] tracking-[0.25em] uppercase text-gold border-b border-gold/50 pb-1 hover:border-gold transition-colors">
                                View the Full Bridal Collection →
                            </Link>
                        </Reveal>
                    </div>
                </section>
            )}

            <section className="py-24 bg-espresso/60 border-t border-gold/10 text-center">
                <div className="max-w-2xl mx-auto px-5">
                    <Reveal>
                        <h2 className="font-serif text-3xl sm:text-4xl">Plan Your <span className="italic gold-text">Bridal Jewellery</span> With Us</h2>
                        <p className="text-sand text-sm mt-5 leading-relaxed">
                            Bring your outfit, your ideas and your family. Our experts will compose a bridal look that is entirely yours.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-9">
                            <button onClick={() => openBooking("Bridal Jewellery")} data-testid="bridal-book-btn" className="bg-gold text-obsidian text-[11px] tracking-[0.25em] uppercase px-9 py-4 hover:bg-gold-bright transition-colors">
                                Book Bridal Consultation
                            </button>
                            <a href={waLink(WA_MESSAGES.bridal)} target="_blank" rel="noreferrer" data-testid="bridal-whatsapp-btn" className="border border-gold/50 text-gold text-[11px] tracking-[0.25em] uppercase px-9 py-4 hover:bg-gold hover:text-obsidian transition-colors">
                                Enquire on WhatsApp
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
};

export default BridalPage;
